import { toast, Typography, Stack } from "@altimateai/lego";
import { DatastoreTypeIcons } from "../datastores/components/DatastoreImage";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { useState } from "react";
import TimeInfoDisclaimer from "../../components/timeInfoDisclaimer";
import { Paginate } from "../../components/paginate";
import { useQuery } from "@tanstack/react-query";
import { getQueryFilters, getAllQueries, getLastFetchedTime } from "@/helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { ColorInsightTags, FilterTagWrapper } from "../../components/tags";
import { GET_LAST_UPDATED_TIME, GET_QUERY } from "./constants";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { parseSortDirection } from "@/components/tables/types";
import { SearchInput } from "../../components/form";
import dayjs from "dayjs";
import { formatNumber } from "@/uiCore";
import { DateRange } from "../../components/dateRange";
import InsightLabel from "./InsightLabel";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import SavedFilters from "@/components/tags/SavedFilters";
import { getFilterStringByMode } from "@/components/tags/utils";
import QueryTextWithHover from "./QueryText";
import QueryHashWithHover from "./QueryHash";
import { DataTable } from "@/components/tables";
import usePaginate from "@/components/paginate/usePaginate";
import { DownloadTable } from "@/components/downloadTable";
import { downloadAllQueryCSV } from "@/helpers/apis";
import SpacesRedirectionButton from "@/components/spaces/SpacesRedirectionButton";
import { SPACES_TAG_MAPPING } from "@/helpers/spacesConstants";
import { FilterEntityType, QueryFilters, FilterParams, QueryItem } from "@/pages/query/types";
import {
  FilterType,
  TableHeaderType,
  SortDirection,
  TextFilterMode,
  createAPIDropdownFilter,
  APIFilterParams,
} from "@/components/tables/types";
import { DatastoresIconMap } from "@/pages/datasets/DatastoreImage";
import { FilterTextChangeValue } from "@/components/filterDropdown";

type DatastoreType = keyof typeof DatastoresIconMap;

const SORT_KEY_MAP: Record<string, keyof typeof INITIAL_SORT_ORDER> = {
  query_cost: "query_cost",
  end_time: "end_time",
  execution_time: "execution_time",
};

const INITIAL_SORT_ORDER = {
  query_cost: "" as SortDirection,
  end_time: "" as SortDirection,
  execution_time: "" as SortDirection,
};

const QueryHeader = () => {
  const [datastoreType, setDatastoreType] = useState<DatastoreType>("snowflake");

  const { data: infoTimeString, isFetching: isLoading } = useQuery({
    queryKey: [GET_LAST_UPDATED_TIME],
    queryFn: getLastFetchedTime,
  });

  return (
    <div className="bg-white p-3 border-radius-top">
      <Stack align="center" gap="lg">
        <Typography variant="page-heading">Queries</Typography>
        <SpacesRedirectionButton tag={SPACES_TAG_MAPPING.QUERY} />
        <DatastoreTypeIcons datastoreType={datastoreType} setDatastoreType={setDatastoreType} />
        <div className="spacer" />
        {!isLoading && infoTimeString && (
          <TimeInfoDisclaimer
            numOfSecondsActive={3}
            textToDisplay={
              <div className="text-muted m-2">
                Queries with execution time {">"}10 secs are shown.
                <br />
                The data was last updated at{" "}
                <span className="text-black fw-semibold">
                  {dayjs
                    .utc(`1970-01-01T${infoTimeString["time_string"]}Z`, "HH:mm:ss")
                    .local()
                    .format("HH:mm:ss A")}{" "}
                  UTC
                </span>
              </div>
            }
          />
        )}
      </Stack>
      <Typography className="mt-2 mb-0" variant="body" size="xxs">
        This page shows history of all the queries run during the selected time period.
      </Typography>
    </div>
  );
};

interface QueryListWithFilterProps {
  setSelectedQuery: (queryId: string) => void;
  queryHash?: string;
}

const QueryListWithFilter = ({ setSelectedQuery, queryHash = "" }: QueryListWithFilterProps) => {
  const { currencySymbol, currency, datastores = [], queryListingDefaultDaysCount } = useAppState();
  const [searchParams] = useSearchParams();
  const { deleteSearchParamsByKeyValue, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { page, pageSize, setPage, updatePageSize, resetPage } = usePaginate();

  const sortAttribute = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  const queryFilters = {
    tags: searchParams.getAll("tags") || [],
    executionTime: searchParams.get("executionTime"),
    queryCost: searchParams.get("queryCost"),
    warehouses: searchParams.getAll("warehouses") || [],
    users: searchParams.getAll("users") || [],
    queryType: searchParams.getAll("queryType") || [],
    queryHash: searchParams.get("queryHash") || "",
    queryParameterizedHash: searchParams.get("queryParameterizedHash") || "",
    sessionID: searchParams.get("sessionID") || "",
    instance: searchParams.getAll("instance") || [],
    teams: searchParams.getAll("teams") || [],
  };
  const tagFilter = queryFilters?.tags || [];
  const executionTimeFilter = queryFilters?.executionTime;
  const queryCostFilter = queryFilters?.queryCost;
  const warehouseFilter = queryFilters?.warehouses || [];
  const userFilter = queryFilters?.users || [];
  const queryTypeFilter = queryFilters?.queryType || [];
  const sessionIDFilter = queryFilters?.sessionID;
  const queryParameterizedHash = queryFilters?.queryParameterizedHash;
  const instanceFilter = queryFilters?.instance || [];
  const teamsFilter = queryFilters?.teams || [];

  const executionTimeFilterMode = searchParams.get("executionTimeFilterMode") || "gt";
  const executionCostFilterMode = searchParams.get("executionCostFilterMode") || "gt";

  const queryId = searchParams.get("queryId") || "";

  const { startDate, endDate } = useDateFromSearchParams(
    dayjs()
      .subtract(queryListingDefaultDaysCount || 28, "day")
      .toDate(),
    dayjs().toDate()
  );

  const handleQueryTypeFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ queryType: value });
  };

  const getFilterParams = (): Partial<FilterParams> => {
    const params: Partial<FilterParams> = {
      page: page + 1,
      size: pageSize,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
    };
    if (queryHash) params.query_hash = queryHash;
    if (queryParameterizedHash) params.query_parameterized_hash = queryParameterizedHash;
    if (queryId) params.query_id = queryId;
    if (warehouseFilter.length > 0) params.warehouses = warehouseFilter;
    if (tagFilter.length > 0) params.tags = tagFilter;
    if (userFilter.length > 0) params.users = userFilter;
    if (instanceFilter.length > 0) params.instance_ids = instanceFilter;
    if (queryTypeFilter.length > 0) params.query_type = queryTypeFilter;
    if (teamsFilter.length > 0) params.teams = teamsFilter;
    if (sortAttribute && sortAttribute in SORT_KEY_MAP) {
      params.sortAttribute = sortAttribute;
      params.sortOrder = sortOrder || "desc";
    }
    if (executionTimeFilter) {
      params.executionTimeFilter = executionTimeFilter;
      params.executionTimeFilterMode = executionTimeFilterMode === "lt" ? "lt" : "gt";
    }
    if (queryCostFilter) {
      params.executionCostFilter = queryCostFilter;
      params.executionCostFilterMode = executionCostFilterMode === "lt" ? "lt" : "gt";
    }
    if (sessionIDFilter) params.session_id = sessionIDFilter;
    params.start_date = getDateByStartOfDayWithoutTimeoffset(startDate).toISOString();
    params.end_date = getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString();
    return params;
  };

  const {
    data: queries = {
      total: 0,
      items: [],
      pages: 0,
      page: 0,
      size: pageSize,
    },
    isFetching: isQueriesLoading,
  } = useQuery({
    queryKey: [
      GET_QUERY,
      page,
      pageSize,
      ...warehouseFilter,
      ...tagFilter,
      ...userFilter,
      ...instanceFilter,
      ...queryTypeFilter,
      ...teamsFilter,
      sortOrder,
      sortAttribute,
      executionTimeFilter,
      queryCostFilter,
      sessionIDFilter,
      queryHash,
      queryParameterizedHash,
      queryId,
      startDate,
      endDate,
      executionTimeFilterMode,
      executionCostFilterMode,
    ],
    queryFn: () => getAllQueries(getFilterParams()),
    meta: {
      onError: (err: unknown) => {
        toast({
          title: "Failed to fetch queries",
          description: `${err instanceof Error ? err.message : String(err)} Please try again.`,
          variant: "destructive",
        });
      },
    },
  });

  const handleDownloadClick = async (): Promise<void> => {
    const params = getFilterParams();
    await downloadAllQueryCSV(params);
  };

  const handleCostFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      queryCost: value,
      executionCostFilterMode: mode,
    });
  };

  const handleExecutionTimeFilterChange = ({ value, mode }: FilterTextChangeValue) => {
    updateSearchParamsByKey({
      executionTime: value,
      executionTimeFilterMode: mode,
    });
  };

  const handleWarehouseFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ warehouses: value });
  };

  const handleUserFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ users: value });
  };

  const handleInsightsFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ tags: value });
  };

  const handleInstanceFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ instance: value });
  };

  const getInstanceName = (instanceId: string, stores: typeof datastores) => {
    const instance = stores.find(d => d.instance_id === instanceId);
    return instance ? instance.name : instanceId;
  };

  const costFilterMode: TextFilterMode = { value: executionCostFilterMode };
  const execTimeFilterMode: TextFilterMode = { value: executionTimeFilterMode };

  const makeQueryFiltersApiFn =
    (entityType: FilterEntityType) =>
    async (params: APIFilterParams): Promise<QueryFilters> => {
      const result = await getQueryFilters({
        entity_type: entityType,
        prefix: params.prefix,
        page: params.page,
      });
      return result as QueryFilters;
    };

  const headers: TableHeaderType<QueryItem>[] = [
    {
      id: 1,
      label: "Query Text",
      uid: "query_text",
      width: "15%",
      render: (item: QueryItem) => (
        <div className={"al-break-words al-text-brand al-cursor-pointer"}>
          <QueryTextWithHover queryText={item.query_trimmed} queryHash={item.query_hash} />
        </div>
      ),
    },
    {
      id: 111,
      label: "Query Hash",
      uid: "query_hash",
      render: (item: QueryItem) => (
        <div className={"al-break-words al-cursor-pointer"}>
          <QueryHashWithHover queryHash={item.query_hash} />
        </div>
      ),
    },
    {
      id: 2,
      label: "Est. Cost",
      uid: "est_cost",
      filter: {
        filterType: FilterType.TEXT,
        value: queryCostFilter ?? undefined,
        placeHolder: `Specify cost in ${currencySymbol}`,
        onChange: handleCostFilterChange,
        label: "Estimate Cost",
        searchKey: "queryCost",
        filterMode: costFilterMode,
        filterModeSearchKey: "executionCostFilterMode",
      },
      sort: {
        sortSearchKey: "sortAttribute",
        sortParamValue: "query_cost",
      },
      render: (item: QueryItem) => (
        <div className={"al-break-all"}>{formatNumber(item.query_cost, { currency })}</div>
      ),
    },
    {
      id: 3,
      label: "Exec. Time",
      uid: "exec_time",
      filter: {
        filterType: FilterType.TEXT,
        value: executionTimeFilter ?? undefined,
        placeHolder: "Specify time in mins",
        onChange: handleExecutionTimeFilterChange,
        label: "Execution Time",
        searchKey: "executionTime",
        filterMode: execTimeFilterMode,
        filterModeSearchKey: "executionTimeFilterMode",
      },
      sort: {
        sortSearchKey: "sortAttribute",
        sortParamValue: "execution_time",
      },
      render: (item: QueryItem) => <div className={"al-break-all"}>{item.execution_time}</div>,
    },
    {
      id: 4,
      label: "Insights",
      uid: "insights",
      width: "15%",
      filter: createAPIDropdownFilter<QueryFilters>({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "tags",
        value: tagFilter,
        apiFunction: makeQueryFiltersApiFn(FilterEntityType.TAGS),
        seeMore: true,
        pageSize: 50,
        onChange: handleInsightsFilterChange,
        extractorFunction: data => {
          return (
            data?.tags?.map(t => ({
              label: <InsightLabel tag={t} />,
              value: t.name,
              showTooltip: false,
            })) || []
          );
        },
      }),
      render: (item: QueryItem) => <ColorInsightTags tags={item?.insights || []} />,
    },
    {
      id: 5,
      label: "Timestamp",
      uid: "timestamp",
      sort: {
        sortSearchKey: "sortAttribute",
        sortParamValue: "end_time",
      },
      render: (item: QueryItem) => <div>{item.end_time}</div>,
    },
    {
      id: 6,
      label: "Query Type",
      uid: "query_type",
      filter: createAPIDropdownFilter<QueryFilters>({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "queryType",
        value: queryTypeFilter,
        apiFunction: makeQueryFiltersApiFn(FilterEntityType.QUERY_TYPES),
        pageSize: 50,
        seeMore: true,
        onChange: handleQueryTypeFilterChange,
        extractorFunction: data => {
          return (
            data?.query_types?.map(qt => ({
              label: qt,
              value: qt,
            })) || []
          );
        },
      }),
      render: (item: QueryItem) => <div className={"al-break-all"}>{item.query_type}</div>,
    },
    {
      id: 7,
      label: "Warehouse",
      uid: "warehouse",
      filter: createAPIDropdownFilter<QueryFilters>({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "warehouses",
        value: warehouseFilter,
        apiFunction: makeQueryFiltersApiFn(FilterEntityType.WAREHOUSES),
        pageSize: 50,
        seeMore: true,
        onChange: handleWarehouseFilterChange,
        extractorFunction: data => {
          return (
            data?.warehouses?.map(w => ({
              label: w,
              value: w,
            })) || []
          );
        },
      }),
      render: (item: QueryItem) => (
        <Link
          to={"/warehouse?warehouse=" + encodeURIComponent(item.warehouse_name)}
          className={classNames("al-break-all", "text-primary al-break-all")}
          onClick={e => e.stopPropagation()}
        >
          {item.warehouse_name}
        </Link>
      ),
    },
    {
      id: 8,
      label: "User",
      uid: "user",
      filter: createAPIDropdownFilter<QueryFilters>({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "users",
        value: userFilter,
        apiFunction: makeQueryFiltersApiFn(FilterEntityType.USERS),
        pageSize: 50,
        seeMore: true,
        onChange: handleUserFilterChange,
        extractorFunction: data => {
          return (
            data?.users?.map(w => ({
              label: w,
              value: w,
            })) || []
          );
        },
      }),
      render: (item: QueryItem) => <div className={"al-break-all"}>{item.user_name}</div>,
    },
    {
      id: 9,
      label: "Instance",
      uid: "instance_id",
      filter: {
        filterType: FilterType.DROPDOWN,
        value: instanceFilter,
        searchKey: "instance",
        options: datastores
          .filter(instance => instance.instance_id != null)
          .map(instance => ({
            label: instance.name,
            value: instance.instance_id as string,
          })),
        onChange: handleInstanceFilterChange,
      },
      render: (item: QueryItem) => (
        <div className={"al-break-all"}>
          {getInstanceName(String(item.instance_id ?? ""), datastores)}
        </div>
      ),
    },
  ];

  if (isQueriesLoading) {
    return <RelativeComponentLoader label={"Loading queries..."} />;
  }

  return (
    <Stack direction="column" gap="md" className="p-3 bg-white border-radius-bottom">
      <Stack gap="md" align="center">
        {queryHash && <div className="fs-4 fw-500 me-3">Query Executions</div>}
        <SearchInput
          defaultValue={queryId}
          onChange={(v: string) => {
            updateSearchParamsByKey({ queryId: v });
          }}
          placeholder="Enter query id"
        />
        {queryId && queries.total === 0 && (
          <div className="text-muted">
            The query id may be outdated, or it will be retrieved within the next day
          </div>
        )}
        <div className="spacer" />
        <SavedFilters applicableFor={FilterApplicableFor.QUERY} />
        <DateRange
          startDate={startDate}
          endDate={endDate}
          availableStaticRanges={["Last day", "Last 7 days", "Last 28 days"]}
          disableCalendarSelection
          onDateRangeSelect={(s, e) => {
            updateSearchParamsByKey({
              start_date: s?.toISOString(),
              end_date: e?.toISOString(),
            });
          }}
        />
        <DownloadTable handleDownload={handleDownloadClick} />
        <div id="columnpicker-container" />
      </Stack>
      <div className={styles.filter_tags}>
        <FilterTagWrapper
          applicableFor={FilterApplicableFor.QUERY}
          filters={{
            tagFilter: {
              filterStr: tagFilter,
              label: "Tags",
              onclose: () => {},
              searchKey: "tags",
            },
            warehouseFilter: {
              filterStr: warehouseFilter,
              label: "Warehouses",
              onclose: () => {},
              searchKey: "warehouses",
            },
            userFilter: {
              filterStr: userFilter,
              label: "Users",
              onclose: () => {},
              searchKey: "users",
            },
            instanceFilter: {
              searchKey: "instance",
              filterStr: instanceFilter.map(instance => getInstanceName(instance, datastores)),
              label: "Instance",
              onclose: (value?: string) => {
                if (!value) return;
                const instance = datastores.find(d => d.name === value);
                if (!instance) return;
                const newInstanceFilter = instanceFilter.filter(i => i !== instance.instance_id);
                updateSearchParamsByKey({ instance: newInstanceFilter });
              },
            },
            queryTypeFilter: {
              filterStr: queryTypeFilter,
              label: "Query Type",
              onclose: () => {},
              searchKey: "queryType",
            },
            executionTimeFilter: {
              filterStr: getFilterStringByMode(executionTimeFilter, executionTimeFilterMode) || "",
              label: "mins",
              onclose: () => {
                deleteSearchParamsByKeyValue({
                  executionTime: undefined,
                  executionTimeFilterMode: undefined,
                });
              },
              filterData: executionTimeFilter
                ? {
                    executionTime: executionTimeFilter,
                    executionTimeFilterMode,
                  }
                : undefined,
            },
            queryCostFilter: {
              filterStr: getFilterStringByMode(queryCostFilter, executionCostFilterMode) || "",
              label: currencySymbol,
              filterData: queryCostFilter
                ? { queryCost: queryCostFilter, executionCostFilterMode }
                : undefined,
              onclose: () => {
                deleteSearchParamsByKeyValue({
                  queryCost: undefined,
                  executionCostFilterMode: undefined,
                });
              },
            },
            sessionIDFilter: {
              filterStr: sessionIDFilter,
              label: "Session ID",
              onclose: () => {},
              searchKey: "sessionID",
            },
            queryParameterizedHashFilter: {
              filterStr: queryParameterizedHash,
              label: "Parameterized Hash",
              onclose: () => {},
              searchKey: "queryParameterizedHash",
            },
            queryIdFilter: {
              filterStr: queryId,
              label: "Query ID",
              onclose: () => {},
              searchKey: "queryId",
            },
          }}
        />
      </div>
      <DataTable
        header={headers}
        onRowClick={(item: QueryItem) => setSelectedQuery(item.query_id)}
        data={queries}
        resetPage={resetPage}
        applicableFor={FilterApplicableFor.QUERY}
        columnPickerPortal="columnpicker-container"
        headerClass="al-min-w-[1300px]"
        tableBodyClass="al-min-w-[1300px]"
        tableClass="al-overflow-x-auto"
      />
      <Paginate
        itemCount={queries.total}
        page={page}
        pageSize={pageSize}
        numPages={queries.pages ?? 0}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};

const AllQueries = () => {
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const navigate = useNavigate();

  return (
    <Stack direction="column">
      <QueryHeader />
      <div className="divider" />
      <QueryListWithFilter
        setSelectedQuery={v =>
          navigate(
            `/query/${v}${startDate ? "?start_date=" + startDate : ""}${
              endDate ? "&end_date=" + endDate : ""
            }`
          )
        }
      />
    </Stack>
  );
};

export { AllQueries, QueryHeader, QueryListWithFilter };
